/**
 *  FieldPlacementNodeView — 필드 설정(배치) 단계 전용 인라인 필드 NodeView
 *
 *  단일레이어(inline) 구조에서 필드는 평소 renderHTML(정적 박스)로만 그려져
 *  리사이즈 UI 가 없었다. 이 NodeView 는 "필드 설정" 편집 화면에서만 붙어
 *  선택된 필드의 우하단 모서리 핸들을 드래그해 크기(width/height)를 조절하게 한다.
 *
 *  - 박스 외형은 nodes.ts 의 renderHTML(contract-field-inline)과 동일하게 맞춘다.
 *  - 값 저장은 node attrs(width/height) → docJson 에 그대로 실린다.
 *  - 서명/렌더/베이크 경로에는 이 NodeView 를 붙이지 않는다(정적 renderHTML 유지).
 */
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useRef } from "react";
import { NodeSelection } from "@tiptap/pm/state";
import {
  fieldBoxSize,
  fieldColor,
  fieldIcon,
  friendlyFieldLabel,
} from "./nodes";

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

/** 드래그(이동/리사이즈) 시작·종료를 알려 필드 팝오버가 잠시 숨도록 한다. */
const emitFieldDrag = (dragging: boolean) => {
  try {
    window.dispatchEvent(
      new CustomEvent("contract-field-drag", { detail: dragging }),
    );
  } catch {
    /* ignore */
  }
};

export const FieldPlacementNodeView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
  updateAttributes,
  selected,
}) => {
  const a = node.attrs;
  const label = friendlyFieldLabel(a.fieldType, a.label, a.fieldKey);
  const color = fieldColor(a.fieldType);
  const sz = fieldBoxSize(a.fieldType, a.width, a.height);
  const boxRef = useRef<HTMLSpanElement | null>(null);

  const selectSelf = () => {
    if (!editor || typeof getPos !== "function") return;
    const pos = getPos();
    if (!Number.isFinite(pos)) return;
    try {
      const tr = editor.state.tr.setSelection(
        NodeSelection.create(editor.state.doc, pos as number),
      );
      editor.view.dispatch(tr);
    } catch {
      /* 선택 실패는 무시 — 팝오버만 안 뜰 뿐 */
    }
  };

  /* 우상단 모서리 드래그 → width/height 조절 (오른쪽=넓게, 위=높게) */
  const onHandleDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    selectSelf();
    emitFieldDrag(true);
    const boxEl = boxRef.current;
    if (!boxEl) return;
    // 화면 배율(줌) 보정 — 렌더된 px / 문서상 px
    const rect = boxEl.getBoundingClientRect();
    const scale = sz.w > 0 ? rect.width / sz.w || 1 : 1;
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = sz.w;
    const startH = sz.h;
    let nextW = startW;
    let nextH = startH;
    let raf = 0;

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      // 우상단 핸들: 오른쪽으로 끌면 넓게(+dx), 위로 끌면 높게(-dy)
      nextW = clamp(Math.round(startW + (ev.clientX - startX) / scale), 40, 600);
      nextH = clamp(Math.round(startH - (ev.clientY - startY) / scale), 18, 400);
      if (!raf) {
        raf = requestAnimationFrame(() => {
          raf = 0;
          updateAttributes({ width: nextW, height: nextH });
        });
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      if (raf) cancelAnimationFrame(raf);
      updateAttributes({ width: nextW, height: nextH });
      emitFieldDrag(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  /* 박스 드래그 → 본문 흐름 내 위치 이동 (드롭 지점의 인라인 자리로 재배치) */
  const onBoxDown = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const tgt = e.target as HTMLElement;
    if (tgt.closest?.("[data-resize-handle]")) return; // 리사이즈는 별도 처리
    selectSelf();
    const boxEl = boxRef.current;
    if (!boxEl || !editor || typeof getPos !== "function") return;
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;
    // setPointerCapture 는 쓰지 않는다 — 캡처가 window 리스너로의 이벤트 전달을 가로챈다.
    // (리사이즈 핸들과 동일하게 window 전역 리스너로만 추적)
    const onMove = (ev: PointerEvent) => {
      if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) > 4) {
        moved = true;
        boxEl.style.opacity = "0.5";
        boxEl.style.cursor = "grabbing";
        emitFieldDrag(true); // 이동 시작 → 팝오버 숨김
      }
    };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      boxEl.style.opacity = "";
      boxEl.style.cursor = "grab";
      if (moved) emitFieldDrag(false); // 이동 종료 → 팝오버 복귀
      if (!moved) return; // 클릭만 — 선택은 이미 처리됨
      const from = getPos();
      if (!Number.isFinite(from)) return;
      const view = editor.view;
      const node = view.state.doc.nodeAt(from as number);
      if (!node) return;
      const coords = view.posAtCoords({ left: ev.clientX, top: ev.clientY });
      if (!coords) return;
      const to = coords.pos;
      // 드롭 지점이 인라인(문단) 내부가 아니면 이동 취소
      const $to = view.state.doc.resolve(to);
      if (!$to.parent.inlineContent) return;
      // 원래 자리 범위면 이동 없음
      if (to >= (from as number) && to <= (from as number) + node.nodeSize)
        return;
      try {
        let tr = view.state.tr.delete(
          from as number,
          (from as number) + node.nodeSize,
        );
        const mapped = tr.mapping.map(to);
        tr = tr.insert(mapped, node);
        tr = tr.setSelection(NodeSelection.create(tr.doc, mapped));
        view.dispatch(tr);
      } catch {
        /* 유효하지 않은 드롭 위치면 무시 */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return (
    <NodeViewWrapper
      as="span"
      className="contract-field-inline"
      style={{
        display: "inline-flex",
        verticalAlign: "middle",
        position: "relative",
      }}
      data-contract-field="true"
      data-field-type={a.fieldType}
    >
      <span
        ref={boxRef}
        onPointerDown={onBoxDown}
        title={selected ? "드래그해서 위치 이동" : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: `${sz.w}px`,
          height: `${sz.h}px`,
          margin: "0 2px",
          border: `2px ${selected ? "solid" : "dashed"} ${color}`,
          color,
          borderRadius: 4,
          background: "#fff",
          fontWeight: 600,
          fontSize: 12,
          lineHeight: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          boxShadow: selected ? `0 0 0 2px ${color}22` : undefined,
          cursor: selected ? "grab" : "pointer",
          touchAction: "none",
        }}
      >
        {`${fieldIcon(a.fieldType)} ${label}`}
      </span>
      {/* 선택 시 우상단 리사이즈 핸들 — 모서리를 잡고 늘려 크기 조절 */}
      {selected && (
        <span
          data-resize-handle="true"
          onPointerDown={onHandleDown}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          title="드래그해서 크기 조절"
          style={{
            position: "absolute",
            right: -4,
            top: -4,
            width: 14,
            height: 14,
            borderRadius: 4,
            background: color,
            border: "2px solid #fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            cursor: "nesw-resize",
            zIndex: 20,
            touchAction: "none",
          }}
        />
      )}
    </NodeViewWrapper>
  );
};
